import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker, Button } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { removeProcessingFifDsr, setProcessingFifDsr } from "store/fif/fifDsrSlice";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";
import moment from "moment";

const UploadFifDsrPage = () => {
  useDocumentTitle("Upload FIF DSR");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingFifDsr } = useAppSelector((state) => state.fifDsr);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<string>();
  const [form] = Form.useForm();

  // Pre-select today's date
  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    setDeliveryDate(today);
    // Set initial value in form
    form.setFieldsValue({
      deliveryDate: moment()
    });
  }, [form]);

  // Handle navigation to save page and auto-cleanup
  useEffect(() => {
    const { timeUploaded } = processingFifDsr;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      // Clear after 15 minutes
      if (minsPassed >= 15) {
        dispatch(removeProcessingFifDsr());
      }
    }

    // Navigate to save page when upload is successful (has key)
    if (processingFifDsr && processingFifDsr.key) {
      navigate(`/${routes.FIF.SAVE_DSR}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingFifDsr, navigate, dispatch]);

  useEffect(() => {
    const failed = processingFifDsr?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingFifDsr?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: any) => {
    dispatch(setProcessingFifDsr(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingFifDsr());
  };

  const { headerMapping } = processingFifDsr;
  const headerMappingWithError = headerMapping ? headerMapping + ",errors:Errors" : "";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingFifDsr?.successRows || [];
  const failedRows = processingFifDsr?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<any & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: any & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors
            ?.map((error: any) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: any & { key: string }) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  const handleDateChange = (date: any) => {
    if (date) {
      setDeliveryDate(date.format("YYYY-MM-DD"));
    }
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload FIF DSR" />
        </Col>
      </Row>
      <Row>
        <Form style={{ width: "80%" }} form={form}>
          <Form.Item label="Select Delivery Date" name="deliveryDate" required>
            <DatePicker
              onChange={handleDateChange}
              showToday={false}
              disabledDate={(current) => {
                // Allow only today and tomorrow, disable all other dates
                if (!current) return false;
                const today = moment().startOf("day");
                const tomorrow = moment().add(1, "day").startOf("day");
                const currentDate = current.startOf("day");
                return !currentDate.isSame(today) && !currentDate.isSame(tomorrow);
              }}
            />
          </Form.Item>
        </Form>
        <span className="mr-4"></span>
        <div>
          <Button type="primary">
            Download Sample File
          </Button>
        </div>
      </Row>

      {deliveryDate && (
        <Row>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={apis.uploadFifDsr}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ deliveryDate }}
            />
          </Col>
        </Row>
      )}

      {!!Object.keys(processingFifDsr).length && !processingFifDsr.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>

          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            ></Table>
          </Row>
        </>
      )}
    </>
  );
};

export default UploadFifDsrPage;
